<script setup lang="ts">
import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { cva } from 'class-variance-authority'
import { Status, StatusType } from '@injectivelabs/utils'
import type { ClassValue } from 'clsx'
import type { VariantProps } from 'class-variance-authority'

const button = cva(
  'flex items-center justify-center transition-all rounded-md font-medium font-sans border disabled:cursor-not-allowed sm:whitespace-nowrap',
  {
    variants: {
      size: {
        xs: 'py-1 px-2 text-xs',
        sm: 'py-1 px-3 text-xs',
        md: 'py-2 px-6 text-sm',
        lg: 'py-2 px-8 text-sm'
      },
      variant: {
        'primary-cta':
          'bg-transparent text-primary-500 border-transparent hover:bg-primary-500 hover:bg-primary-500/20',

        'danger-cta':
          'bg-transparent text-red-500 border-transparent hover:bg-red-500 hover:bg-red-500/20 focus-within:ring-0',

        'success-cta':
          'bg-transparent text-green-500 border-transparent hover:bg-green-500 hover:bg-green-500/20 focus-within:ring-0',

        'danger-ghost':
          'bg-transparent text-neutral-200 border-transparent hover:text-white hover:bg-red-500/20 disabled:bg-transparent disabled:text-neutral-400 focus-within:ring-[3px] ring-red-700',

        danger:
          'bg-red-500 text-red-900 border-red-500 hover:text-red-900 hover:bg-red-500/70 disabled:bg-transparent disabled:text-neutral-400 disabled:border-red-500 focus-within:ring-[3px] ring-red-700',

        success:
          'bg-green-500 text-green-900 border-green-500 hover:text-green-900 hover:bg-green-500/70 disabled:bg-transparent disabled:text-neutral-400 disabled:border-green-500 focus-within:ring-[3px] ring-green-700',

        primary:
          'bg-primary-500 text-neutral-950 border-primary-500 hover:bg-primary-500/70 hover:border-primary-500/70 disabled:bg-transparent disabled:text-neutral-400 disabled:border-primary-500 focus-within:ring-[3px] ring-primary-700',

        'danger-outline':
          'bg-transparent text-neutral-200 border-red-500 hover:text-white hover:bg-red-500/20 disabled:bg-transparent disabled:text-neutral-400 disabled:border-red-500 focus-within:ring-[3px] ring-red-700',

        'primary-ghost':
          'bg-transparent text-neutral-200 border-transparent hover:text-white hover:bg-primary-500/20 disabled:bg-transparent disabled:text-neutral-400 disabled:border-primary-500 focus-within:ring-[3px] ring-primary-700',

        'primary-outline':
          'bg-transparent text-neutral-100 border-primary-500 hover:text-white hover:bg-primary-500/20 disabled:bg-transparent disabled:text-neutral-400 disabled:border-neutral-400 focus-within:ring-[3px] ring-primary-700',

        'success-outline':
          'bg-transparent text-neutral-200 border-green-500 hover:text-white hover:bg-green-500/20 disabled:bg-transparent disabled:text-neutral-400 disabled:border-green-500 focus-within:ring-[3px] ring-green-700',

        'success-ghost':
          'bg-transparent text-neutral-200 border-transparent hover:text-white hover:bg-green-500/20 disabled:bg-transparent disabled:text-neutral-400 disabled:border-green-500 focus-within:ring-[3px] ring-green-700',

        'danger-shade':
          'bg-red-500/20 border-red-500 hover:text-red-900 hover:bg-red-500/70 disabled:bg-transparent disabled:text-neutral-400 disabled:border-red-500 ring-red-700 text-red-500 border-none px-3 focus-within:none'
      }
    }
  }
)

export type ButtonProps = VariantProps<typeof button>

defineOptions({
  inheritAttrs: false
})

const props = withDefaults(
  defineProps<{
    status?: Status
    tooltip?: string
    disabled?: boolean
    isLoading?: boolean
    isFullWidth?: boolean
    size?: ButtonProps['size']
    variant?: ButtonProps['variant']
    class?: ClassValue | ClassValue[]
  }>(),
  {
    size: 'md',
    tooltip: '',
    class: () => [],
    variant: 'primary',
    status: () => new Status(StatusType.Idle)
  }
)
</script>

<template>
  <UTooltip
    :content="{ side: 'top' }"
    :disabled="!tooltip && !$slots.content"
    :ui="{
      content: `text-sm dark:bg-black/80 w-fit ${isFullWidth ? 'relative block w-full' : 'relative block'}`
    }"
  >
    <button
      :class="twMerge(button({ size, variant }), clsx(props.class))"
      :disabled="disabled"
      v-bind="$attrs"
    >
      <span v-if="status.isLoading() || isLoading">&#8202;</span>
      <AppSpinner v-if="status.isLoading() || isLoading" is-sm is-white />

      <slot v-else />
    </button>

    <template #content>
      <slot name="content">
        <span>{{ tooltip }}</span>
      </slot>
    </template>
  </UTooltip>
</template>
